import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { listMyEvents, deleteMyEvent } from '../lib/api.js'
import { conferenceLabel } from '../lib/text.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import useMe from '../hooks/useMe.js'
import { PAGE_STANDARD } from '../lib/layout.js'
import { BTN_PRIMARY, BTN_ROW, BTN_ROW_DANGER } from '../lib/ui.js'

const STATUS_BADGE = {
  DRAFT: 'bg-amber-100 text-amber-800',
  TENTATIVE: 'bg-sky-100 text-sky-800',
  PUBLISHED: 'bg-emerald-100 text-emerald-800',
  CANCELLED: 'bg-rose-100 text-rose-800',
}

function formatDate(iso) {
  if (!iso) return ''
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  }).format(new Date(iso))
}

export default function MyEventsPage() {
  const { me } = useMe()
  const isAdmin = me?.role === 'ADMIN'
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    listMyEvents()
      .then(setRows)
      .catch((err) => setError(err.message || String(err)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const remove = async (id, title) => {
    if (!window.confirm(`Soft-delete "${title}"? It can be restored by an admin.`)) return
    try {
      await deleteMyEvent(id)
      setRows((prev) => prev.filter((e) => e.id !== id))
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  const canEdit = (event) => isAdmin || event.speakers?.find((s) => s.isMain)?.userId === me?.userId

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <span className="brand-gradient-text">My events</span>
          </h1>
          <p className="mt-2 text-base text-slate-600">
            Talks where you are the main speaker or a co-speaker. Drafts are visible only here.
          </p>
        </div>
        <Link
          to="/admin/events/new"
          className={BTN_PRIMARY}
        >
          New event
        </Link>
      </header>

      {error ? <ErrorAlert title="Couldn't load events" message={error} source="MyEventsPage" /> : null}

      {loading ? (
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200/60">
          <h3 className="text-base font-semibold text-slate-900">No events yet</h3>
          <p className="mt-1 text-sm text-slate-500">Use "New event" to add one.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((e) => {
            const main = (e.speakers || []).find((s) => s.isMain)
            const co = (e.speakers || []).filter((s) => !s.isMain)
            const editable = canEdit(e)
            return (
              <li key={e.id} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/60 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[e.status] || 'bg-slate-100 text-slate-700'}`}>
                        {e.status}
                      </span>
                      {e.format ? (
                        <span className="text-xs text-slate-500">{e.format.toLowerCase().replace('_', '-')}</span>
                      ) : null}
                      {!editable ? (
                        <span className="text-xs text-slate-400">view-only (you're a co-speaker)</span>
                      ) : null}
                    </div>
                    <h2 className="mt-1.5 text-lg font-semibold leading-snug tracking-tight text-slate-900">
                      {e.title}
                    </h2>
                    {/* #341: nested object since #307 — rendering it raw threw
                        "Objects are not valid as a React child". */}
                    {conferenceLabel(e) ? (
                      <div className="mt-0.5 text-sm text-slate-500">{conferenceLabel(e)}</div>
                    ) : null}
                    <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                      <div><dt className="inline text-slate-500">When: </dt><dd className="inline">{formatDate(e.startsAtUtc)}</dd></div>
                      {(e.city || e.country) ? <div><dt className="inline text-slate-500">Where: </dt><dd className="inline">{[e.city, e.country].filter(Boolean).join(', ')}</dd></div> : null}
                      {main ? <div><dt className="inline text-slate-500">Main: </dt><dd className="inline">{main.displayName || `User #${main.userId}`}</dd></div> : null}
                      {co.length ? <div><dt className="inline text-slate-500">With: </dt><dd className="inline">{co.map((s) => s.displayName || `User #${s.userId}`).join(', ')}</dd></div> : null}
                    </dl>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/admin/events/${e.id}`}
                      className={BTN_ROW}
                    >
                      {editable ? 'Edit' : 'View'}
                    </Link>
                    {editable ? (
                      <button
                        type="button"
                        onClick={() => remove(e.id, e.title)}
                        className={BTN_ROW_DANGER}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </main>
  )
}
